import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AuthContextType, LanguageCode, UserAccount } from './types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function apiRequest(path: string, options: RequestInit = {}) {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'Die Anfrage ist fehlgeschlagen.');
  return payload;
}

function withLocalPhoto(user: UserAccount | null): UserAccount | null {
  if (!user || typeof localStorage === 'undefined') return user;
  const photoURL = localStorage.getItem(`lumi_account_photo_${user.uid}`) || undefined;
  return photoURL ? { ...user, photoURL } : user;
}

// Locale is a privacy-friendly first guess for the registration country. It
// avoids asking a parent for location access, and the server only accepts an
// ISO country code (or leaves it empty when the browser has no region).
function browserCountry() {
  try {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    return new Intl.Locale(locale).region || undefined;
  } catch {
    return undefined;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserAccount | null>(null);
  const [loading, setLoading] = useState(true);

  const applyUser = useCallback((nextUser: UserAccount | null) => {
    setUser(withLocalPhoto(nextUser));
  }, []);

  const refreshUser = useCallback(async () => {
    const payload = await apiRequest('/api/auth/session');
    applyUser(payload.user || null);
    return payload.user || null;
  }, [applyUser]);

  useEffect(() => {
    refreshUser().catch(() => applyUser(null)).finally(() => setLoading(false));
  }, [refreshUser, applyUser]);

  const login = async (email: string, pass: string) => {
    setLoading(true);
    try {
      const payload = await apiRequest('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password: pass }),
      });
      applyUser(payload.user);
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email: string, pass: string, name: string, adultConfirmed: boolean, language: LanguageCode, marketingConsent = false) => {
    setLoading(true);
    try {
      const payload = await apiRequest('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password: pass, name, adultConfirmed, language, country: browserCountry(), marketingConsent }),
      });
      applyUser(payload.user);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await apiRequest('/api/auth/logout', { method: 'POST', body: '{}' });
      applyUser(null);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    await apiRequest('/api/auth/password/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  };

  const completePasswordReset = async (token: string, password: string) => {
    setLoading(true);
    try {
      const payload = await apiRequest('/api/auth/password/reset', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      applyUser(payload.user);
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword: string, password: string) => {
    const payload = await apiRequest('/api/auth/password/change', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, password }),
    });
    applyUser(payload.user);
  };

  const verifyEmail = async (token: string) => {
    const payload = await apiRequest('/api/auth/email/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    applyUser(payload.user);
  };

  const resendEmailVerification = async () => {
    const payload = await apiRequest('/api/auth/email/resend', {
      method: 'POST',
      body: '{}',
    });
    return payload;
  };

  const deleteAccount = async () => {
    setLoading(true);
    try {
      await apiRequest('/api/auth/account', { method: 'DELETE' });
      if (user) localStorage.removeItem(`lumi_account_photo_${user.uid}`);
      applyUser(null);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (data: Partial<UserAccount>) => {
    if (!user) return;
    let nextUser = { ...user, ...data };
    if (data.displayName || data.preferences) {
      const payload = await apiRequest('/api/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify({ displayName: data.displayName, preferences: data.preferences, marketingConsent: data.marketingConsent }),
      });
      nextUser = { ...nextUser, ...payload.user };
    }
    // Account avatars stay only on this browser. Checking for the property
    // rather than its truthiness makes an explicit "remove photo" action
    // delete the local value as well.
    if (Object.prototype.hasOwnProperty.call(data, 'photoURL')) {
      if (data.photoURL) localStorage.setItem(`lumi_account_photo_${user.uid}`, data.photoURL);
      else localStorage.removeItem(`lumi_account_photo_${user.uid}`);
    }
    applyUser(nextUser);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      signup,
      logout,
      resetPassword,
      completePasswordReset,
      changePassword,
      verifyEmail,
      resendEmailVerification,
      deleteAccount,
      updateProfile,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
